import React, { useEffect, useState } from 'react'
import {
  Input,
  BUTTON_THEME,
  CircularLoader,
  Button,
  Select,
} from '@st-operations/uikit/src/v2/index.js'
import { getRegions } from '_services/regions'

import User from '../../lib/User'
import Header from '_components/header/index.jsx'
import { useRetractSidebar } from '_hooks'
import sendAnalytics from '_root/lib/sendAnalytics.js'
import { getUrlSimilaritySeeds, updateUrlSimilaritySeeds } from '_services/data-explorer'
import { logErrorAsync } from '../../lib/utility'
import MultiValueInput from '../multi-value-input'
import UrlSimilaritySeedTopUrls from '../url-similarity-seed-top-urls'
import MediaMeasurementCampaignsStatusBadge from '../media-measurement-campaign-status-badge'
import { postCustomAudience } from '_services/audience'
import convertObjAttrsSnakeToCamelCase from '_lib/convertObjAttrsSnakeToCamelCase'

import './styles.scss'

const ENTITY = 'url-similarity-seeds'

const UrlSimilaritySeedPage = (props) => {
  const { id } = props.match.params

  const [data, setData] = useState()
  const [inRequest, setInRequest] = useState(false)
  const [regions, setRegions] = useState([])

  useRetractSidebar()

  useEffect(() => {
    const load = async (id) => {
      try {
        const data = await getUrlSimilaritySeeds(id)
        setData(data)
        sendAnalytics('read', ENTITY, data)
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }

    load(id)
  }, [id])

  useEffect(() => {
    async function getData() {
      try {
        const regionsResponse = await getRegions({
          fields: ['_id', 'name', 'countries'],
          limit: 1000,
        })

        setRegions(regionsResponse.items.sort((a, b) => (a.name > b.name ? 1 : -1)))
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }
    getData()
  }, [])

  const handleSaveAs = async (status = 'draft') => {
    if (inRequest) return
    setInRequest('Updating')
    const { _id, email } = User.getProfile()

    const newData = {
      ...data,
      status,
      updated_at: new Date().toISOString(),
      updated_by: { _id, email },
    }

    try {
      await updateUrlSimilaritySeeds(id, newData)
      toast.success('Update successful!')
      handleExit()
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
      setInRequest(false)
    }

    sendAnalytics('update', ENTITY, newData)
  }

  const handleExit = () => {
    props.history.push(`/${ENTITY}`)
  }

  if (!data) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <CircularLoader />
      </div>
    )
  }

  return (
    <>
      <Header
        title={
          <div className="flex flex-row gap-4 items-center">
            <span>URL Similarity Seed</span>
            <MediaMeasurementCampaignsStatusBadge status={data.status} />
          </div>
        }
      >
        <Button theme={BUTTON_THEME.GHOST} onClick={handleExit}>
          Exit
        </Button>
        <If condition={data.status === 'draft'}>
          <Button theme={BUTTON_THEME.SECONDARY} onClick={() => handleSaveAs('draft')}>
            Save as Draft
          </Button>
        </If>
        <If condition={data.status === 'processed'}>
          <Button
            theme={BUTTON_THEME.SECONDARY}
            onClick={async () => {
              const { _id } = await createAudienceFromProcessedSeed(data)
              props.history.push(`/audience-creator/${_id}`)
            }}
          >
            Create an Audience
          </Button>
        </If>
        <If condition={data.status === 'draft' || data.status === 'processed'}>
          <Button theme={BUTTON_THEME.PRIMARY} onClick={() => handleSaveAs('submitted')}>
            Submit
          </Button>
        </If>
      </Header>
      <div>
        <Input
          label="Name"
          value={data.name}
          onChange={(value) => setData({ ...data, name: value })}
        />
        <Select
          label="Regions"
          className="audience-creator__regions-field"
          isMulti
          isSearchable
          useReactSelect
          name="regions"
          options={regions?.filter(
            (region) =>
              !(data.regions || [])?.some((selectedRegion) => selectedRegion.name === region.name)
          )}
          value={(data.regions || [])?.sort((a, b) => (a.name > b.name ? 1 : -1))}
          onChange={(regions) => setData({ ...data, regions })}
        />
        <MultiValueInput
          className="url-similarity-seed__multivalue"
          label="URLs"
          values={data.urls}
          onChange={(value) => setData({ ...data, urls: value })}
        />
        <If condition={data.status === 'processed'}>
          <UrlSimilaritySeedTopUrls className="mt-8" topUrls={data.topUrls} />
        </If>
      </div>
    </>
  )
}

export async function createAudienceFromProcessedSeed(seed) {
  return await postCustomAudience({
    name: `ShareThis > Interest from Social Activity > ${seed.name}`,
    destinations: [
      {
        _id: '632dec466420cb001a723e60',
        name: 'LiveRamp US 2',
        slug: 'liveramp-us-2',
      },
    ],
    topics: [
      {
        atoms: {
          seeds: [convertObjAttrsSnakeToCamelCase(seed).embeddingPath],
        },
        name: seed.name,
      },
    ],
    seed,
    threshold: { label: 'balanced', value: seed.thresholds.balanced.value },
    regions: seed.regions,
  })
}

export default UrlSimilaritySeedPage
